import type { CmsAccordionData, CmsAccordionProps } from "@chewy/cms-accordion";
import type { CmsBannerData, CmsBannerProps } from "@chewy/cms-banner";
import type { CmsComboWidgetProps } from "@chewy/cms-combo-widget";
import type { CmsContentBlockData, CmsContentBlockProps } from "@chewy/cms-content-block";
import type { CmsDeckData, CmsDeckProps } from "@chewy/cms-deck";
import type { CmsEducationalData, CmsEducationalProps } from "@chewy/cms-educational";
import type { CmsHpHeroCarouselData, CmsHpHeroCarouselProps } from "@chewy/cms-hp-hero-carousel";
import type { CmsNoContentData } from "@chewy/cms-no-content";
import type { CmsNonSkuData, CmsNonSkuProps } from "@chewy/cms-non-sku";
import type { ImageLoadingAttribute } from "@chewy/cms-shared-typings";
import type { CmsSitewideBannerData, CmsSitewideBannerProps } from "@chewy/cms-sitewide-banner";
import type { CmsSkuData, CmsSkuProps } from "@chewy/cms-sku/src/typings";
import { DynatraceActionType, trackDynatraceRumEvent } from "@chewy/storefront-utils";
import { type JSX, lazy, useEffect } from "react";

import SitewideBannerWrapper from "./cms-sitewide-banner/SitewideBannerWrapper";

// Lazy load components
const Accordion = lazy(() => import("@chewy/cms-accordion"));
const Banner = lazy(() => import("@chewy/cms-banner"));
const ComboWidget = lazy(() => import("@chewy/cms-combo-widget"));
const ContentBlock = lazy(() => import("@chewy/cms-content-block"));
const Deck = lazy(() => import("@chewy/cms-deck"));
const Educational = lazy(() => import("@chewy/cms-educational"));
const HpHeroCarousel = lazy(() => import("@chewy/cms-hp-hero-carousel"));
const NoContent = lazy(() => import("@chewy/cms-no-content"));
const NonSku = lazy(() => import("@chewy/cms-non-sku"));
const Sku = lazy(() => import("@chewy/cms-sku"));

enum WidgetVariant {
  ACCORDION = "ACCORDION",
  BANNER = "STANDARD-BANNER",
  CARD = "STANDARD-CARD",
  CONTENT_BLOCK = "STANDARD-CONTENT-BLOCK",
  DECK = "DECK",
  HP_HERO_CAROUSEL = "STANDARD-BANNER-CAROUSEL",
  NON_SKU = "STANDARD-NON-SKU",
  PLACEHOLDER = "STANDARD-PLACEHOLDER",
  SITE_WIDE_BANNER = "STANDARD-SITE-WIDE-BANNER",
  SKU = "STANDARD-SKU",
}

export type CmsComponentData =
  | CmsAccordionData
  | CmsBannerData
  | CmsContentBlockData
  | CmsDeckData
  | CmsEducationalData
  | CmsHpHeroCarouselData
  | CmsNoContentData
  | CmsNonSkuData
  | CmsSitewideBannerData
  | CmsSkuData;
export type CmsComponentOptions = {
  accordion?: CmsAccordionProps;
  banner?: CmsBannerProps;
  contentBlock?: CmsContentBlockProps;
  deck?: CmsDeckProps;
  educational?: CmsEducationalProps;
  hpHeroCarousel?: CmsHpHeroCarouselProps;
  nonSku?: CmsNonSkuProps;
  sitewideBanner?: CmsSitewideBannerProps;
  sku?: CmsSkuProps;
  combo?: CmsComboWidgetProps;
};
export type CmsExperiments = { name: string; variation?: string }[];
export type CmsComponentProps = {
  data: CmsComponentData;
  options?: CmsComponentOptions;
  zoneParams: {
    pageType: string;
    selectionKey: string;
    zoneId: string;
  };
  experiments?: CmsExperiments;
};

const RESILIENCY_TEST_PLACEHOLDER = "resiliency-test-content-mfe";

const PlaceholderErrorComponent = () => {
  throw new Error("Placeholder type is not supported");
};

export function CmsComponent({ data, options, zoneParams, ...props }: CmsComponentProps): JSX.Element | null {
  if (data.instrumentation?.properties) {
    try {
      Object.assign(data.instrumentation?.properties, {
        renderSource: "mfe",
      });
    } catch (error) {
      console.log({ error });
    }
  }

  const DT_EVENT_NAME = `content-mfe:${data.type}`;

  useEffect(() => {
    if (typeof window !== "undefined" && window.performance) {
      const startTime = window.performance.timeOrigin;
      const endTime = Date.now();

      trackDynatraceRumEvent(DynatraceActionType.START, DT_EVENT_NAME, {
        time: startTime,
        debugConsole: false,
      });

      trackDynatraceRumEvent(DynatraceActionType.STOP, DT_EVENT_NAME, {
        time: endTime,
        debugConsole: false,
      });
    }
  }, [DT_EVENT_NAME]);

  switch (data?.type) {
    case WidgetVariant.ACCORDION:
      return <Accordion data={data as CmsAccordionData} {...props} {...options?.accordion} />;
    case WidgetVariant.BANNER: {
      const bannerData = data as CmsBannerData;
      return <Banner data={bannerData} {...props} {...options?.banner} />;
    }
    case WidgetVariant.HP_HERO_CAROUSEL: {
      const hpHeroCarouselData = data as CmsHpHeroCarouselData;
      // CAPO-31: Handle custom zone logic for HP Hero Carousel
      const customHpHeroCarouselItems = hpHeroCarouselData.items?.map((item) => {
        return {
          ...item,
          images: {
            ...item.images,
            loading: "eager" as ImageLoadingAttribute,
          },
        };
      });
      if (zoneParams.pageType === "hp" && zoneParams.zoneId === "1") {
        return (
          <HpHeroCarousel
            data={{ ...hpHeroCarouselData, items: customHpHeroCarouselItems }}
            hasRoundedCorners
            variation="modern"
            {...props}
            {...options?.banner}
          />
        );
      }
      return <HpHeroCarousel data={data as CmsHpHeroCarouselData} {...props} {...options?.hpHeroCarousel} />;
    }
    case WidgetVariant.CONTENT_BLOCK: {
      const contentBlockData = data as CmsContentBlockData;
      // CAPO-37 Wistia Script
      const isUsePlayer =
        (contentBlockData?.items ?? []).find((item) => !!item.wistiaId)?.mediaPlayerConfig?.autoPlay ?? false;
      return <ContentBlock data={contentBlockData} {...props} {...options?.contentBlock} usePlayer={isUsePlayer} />;
    }
    case WidgetVariant.CARD:
      return <Educational data={data as CmsEducationalData} {...props} {...options?.educational} />;
    case WidgetVariant.DECK:
      return <Deck data={data as CmsDeckData} {...props} {...options?.deck} />;
    case WidgetVariant.NON_SKU: {
      return <NonSku data={data as CmsNonSkuData} {...props} {...options?.nonSku} />;
    }
    case WidgetVariant.SITE_WIDE_BANNER:
      return <SitewideBannerWrapper data={data as CmsSitewideBannerData} {...props} {...options?.sitewideBanner} />;
    case WidgetVariant.SKU: {
      const skuData = data as CmsSkuData;
      if ((skuData?.subType || "").match(/combo/i)) {
        return (
          <ComboWidget data={skuData}>
            <Sku data={skuData} hasHeading={false} experiments={props.experiments} {...props} {...options?.sku} />
          </ComboWidget>
        );
      }
      return <Sku data={skuData} {...props} {...options?.sku} />;
    }
    case WidgetVariant.PLACEHOLDER:
      return (data as Record<string, unknown>).placeholderType === RESILIENCY_TEST_PLACEHOLDER ? (
        <PlaceholderErrorComponent />
      ) : null;
    case "NO-CONTENT":
      return <NoContent data={data as CmsNoContentData} />;
    default:
      return <></>;
  }
}
